import type { Prisma } from "@prisma/client";
import { propositionsSelect, propositionDetailsSelect, propositionDocumentsSelect, propositionAbilityControlSelect, propositionStatuses } from "@dune/vars";
import { PaginatedResponse } from "../common/pagination.types";
import { ProjectDocumentGenerationTypeKeys } from "../documents/documents.types";

export const PropositionStatus = {
	NOUVEAU: "NOUVEAU",
	A_SIGNER: "A_SIGNER",
	SIGNEE: "SIGNEE",
	EN_COURS: "EN_COURS",
	CONTRACT_NEW: "CONTRACT_NEW",
  	CONTRACT_TO_SIGN: "CONTRACT_TO_SIGN",
	CONTRACT_ONGOING: "CONTRACT_ONGOING",
	CONTRACT_WARRANTY: "CONTRACT_WARRANTY",
	CONTRACT_TERMINATED: "CONTRACT_TERMINATED",
	SANS_SUITE: "SANS_SUITE",
} as const;

export type PropositionStatusKeys = keyof typeof PropositionStatus;
export type PropositionStatusValues = (typeof PropositionStatus)[keyof typeof PropositionStatus];
export type PropositionStatusType = (typeof propositionStatuses)[number]

export const PropositionProductType = {
	DO: "DO",
	TRC: "TRC",
	MRT: "MRT",
	GFA: "GFA",
	PJ: "PJ",
} as const;

export type PropositionProductTypeKeys = keyof typeof PropositionProductType;
export type PropositionProductTypeValues = (typeof PropositionProductType)[keyof typeof PropositionProductType];

export const propositionDocumentTypeEnum = {
	PROPOSITION_ASSURANCE: "PROPOSITION_ASSURANCE",
	PROPOSITION_ASSURANCE_SIGNED: "PROPOSITION_ASSURANCE_SIGNED",
	CONDITIONS_GENERALES: "CONDITIONS_GENERALES",
	QUITTANCE: "QUITTANCE",
	CONTRAT: "CONTRAT",
	CONTRAT_SIGNED: "CONTRAT_SIGNED",
	QUITTANCEMENT_RECEPTION: "QUITTANCEMENT_RECEPTION",
	ATTESTATION_DEFINITIVE: "ATTESTATION_DEFINITIVE",
	AVENANT_FIN_DE_TRAVAUX: "AVENANT_FIN_DE_TRAVAUX",
	AVENANT_FIN_DE_TRAVAUX_SIGNED: "AVENANT_FIN_DE_TRAVAUX_SIGNED",
	AUTRE: "AUTRE",
} as const;

export type PropositionDocumentType = (typeof propositionDocumentTypeEnum)[keyof typeof propositionDocumentTypeEnum];

export const propositionDocumentTypeLabels: Record<PropositionDocumentType, string> = {
	[propositionDocumentTypeEnum.PROPOSITION_ASSURANCE]:
		"Proposition d'assurance",
	[propositionDocumentTypeEnum.PROPOSITION_ASSURANCE_SIGNED]:
		"Proposition d'assurance signée",
	[propositionDocumentTypeEnum.CONDITIONS_GENERALES]: "Conditions générales",
	[propositionDocumentTypeEnum.QUITTANCE]: "Quittance",
	[propositionDocumentTypeEnum.CONTRAT]: "Contrat",
	[propositionDocumentTypeEnum.CONTRAT_SIGNED]: "Contrat signé",
	[propositionDocumentTypeEnum.QUITTANCEMENT_RECEPTION]:
		"Quittancement de réception",
	[propositionDocumentTypeEnum.ATTESTATION_DEFINITIVE]:
		"Attestation définitive",
	[propositionDocumentTypeEnum.AVENANT_FIN_DE_TRAVAUX]:
		"Avenant fin de travaux",
	[propositionDocumentTypeEnum.AVENANT_FIN_DE_TRAVAUX_SIGNED]:
		"Avenant fin de travaux signé",
	[propositionDocumentTypeEnum.AUTRE]: "Autre",
};

export type PropositionAbilityControl = Prisma.PropositionGetPayload<{
	select: typeof propositionAbilityControlSelect;
}>;

// Infer Proposition type directly from the selector
export type Proposition = Prisma.PropositionGetPayload<{
	select: typeof propositionsSelect;
}>;

// Infer PropositionDetails type directly from the selector
export type PropositionDetails = Prisma.PropositionGetPayload<{
	select: typeof propositionDetailsSelect;
}>;

export type PropositionsList = Proposition[];

export type PaginatedPropositions = PaginatedResponse<Proposition>;

export interface UpdatePropositionDto {
	status?: PropositionStatusValues;
	assureurName?: string;
	primeTTC?: number;
	remunerationApporteur?: number;
	reservesActive?: boolean;
	reservesDescription?: string[];
}

export interface LiftReservesDto {
	reservesToLift: string[];
}

export interface PropositionFiltersDto {
	status?: PropositionStatusValues;
	productType?: PropositionProductTypeValues;
	constructionSiteId?: number;
	brokerSocietyId?: number;
	search?: string;
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}

export interface GenerateFromSubmissionDto {
    constructionSiteSubmissionId: number;
    reservesDescription?: string[];
}

export interface CreatePropositionDocumentDto {
	propositionId: number;
	fileId: string;
	type: PropositionDocumentType;
}

export interface GeneratePropositionDocumentDto {
	templateId: string;
	signataireDuneId?: number;
	format?: "PDF" | "DOCX";
}

export interface GenerateDocumentDto {
	type: ProjectDocumentGenerationTypeKeys;
	templateId: string;
	signataireDuneId?: number;
	format?: "PDF" | "DOCX";
	skipNextTasks?: boolean;
}

export interface GenerateQuittanceDto {
	templateId: string;
	signataireDuneId?: number;
	format?: "PDF" | "DOCX";
	skipTaskValidation?: boolean;
}

export interface DocumentGenerationConfig {
	type: ProjectDocumentGenerationTypeKeys;
	templateId: string;
	format?: "PDF" | "DOCX";
}

export interface GenerateMultipleDocumentsDto {
	documents: DocumentGenerationConfig[];
	signataireDuneId?: number;
	skipNextTasks?: boolean;
	completeGfaGenerationTask?: boolean;
}

export interface GenerateMultipleDocumentsResponse {
	documents: Array<{
		type: ProjectDocumentGenerationTypeKeys;
		documentUrl: string;
		documentId: number;
		fileId: number | string;
	}>;
	message: string;
}


export type PropositionDocument = Prisma.PropositionDocumentGetPayload<{
	select: typeof propositionDocumentsSelect;
}>;

export interface PropositionReferenceParams {
	year?: number;
	constructionSiteId: number;
}

export interface PropositionCounts {
	statusCounts: Record<string, number>;
	productTypeCounts: Record<string, number>;
}
